import React from 'react';
import dayjs from 'dayjs';
import StylishDateTimePicker from 'components/DesignSystems/form/StylishDateTimePicker';

export type PeriodSectionProps = {
  title: string;
  badge: React.ReactNode;
  summary?: string;
  highlight?: boolean;
  headerAction?: React.ReactNode;
  /** Sections are titled by a period number, so tests need a stable handle. */
  testId?: string;
  children: React.ReactNode;
};

export function PeriodSection({
  title,
  badge,
  summary,
  highlight,
  headerAction,
  testId,
  children,
}: PeriodSectionProps) {
  return (
    <div
      data-testid={testId}
      className={[
        'p-md-4 mb-3 rounded p-3',
        'bg-gray-800',
        highlight ? 'border-primary border-opacity-50 border' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="d-flex align-items-start justify-content-between flex-wrap gap-2">
        <div>
          <div className="d-flex align-items-center flex-wrap gap-2">
            <h3 className="h5 mb-0 text-white">{title}</h3>
            {badge}
          </div>
          {summary ? (
            <p className="text-weak small mt-2 mb-0">{summary}</p>
          ) : null}
        </div>
        {headerAction}
      </div>
      <div className="mt-3">{children}</div>
    </div>
  );
}

export function EditableTimeTile({
  label,
  valueIso,
  onChangeIso,
  disabled,
  testId,
}: {
  label: string;
  valueIso: string | null;
  onChangeIso: (iso: string | null) => void;
  disabled: boolean;
  /**
   * The same labels appear on more than one period, and the picker inside
   * hardcodes its DOM id, so a tile needs its own handle to be addressable.
   */
  testId?: string;
}) {
  return (
    <div data-testid={testId} className="bg-dark bg-opacity-25 rounded p-3">
      <div className="text-uppercase txt--sml text-weak mb-3">{label}</div>
      <div className="form-label">Date &amp; time</div>
      <StylishDateTimePicker
        value={
          valueIso && dayjs(valueIso).isValid()
            ? dayjs(valueIso).toDate()
            : null
        }
        onChange={(newValue) => {
          const v = newValue as Date | string | null;
          if (v instanceof Date) {
            onChangeIso(v.toISOString());
          } else if (typeof v === 'string') {
            onChangeIso(v);
          } else {
            onChangeIso(null);
          }
        }}
        type="datetime-local"
        disabled={disabled}
      />
    </div>
  );
}

export function ReadOnlyTimeTile({
  label,
  valueFormatted,
}: {
  label: string;
  valueFormatted: string;
}) {
  return (
    <div className="bg-dark bg-opacity-25 rounded p-3">
      <div className="text-uppercase txt--sml text-weak">{label}</div>
      <div className="fw-medium mt-1 text-white">{valueFormatted}</div>
    </div>
  );
}
